import { useState } from 'react';
import type { ApiEndpoints, BillOfSaleEventWithDocument } from '@avisoma/shared';
import { FaFile } from 'react-icons/fa6';
import { Card } from '../../components/Card';
import { DescriptionList, DescriptionTerm, DescriptionDetails } from '../../catalyst/description-list';
import { ExtractionTableCard } from '../../components/reconstructedDocument/extractionTables/ExtractionTableCard';
import { RowInspectorDialog } from '../../components/reconstructedDocument/extractionTables/RowInspectorDialog';
import { formatBillOfSaleSigned } from '../../components/reconstructedDocument/extractionTables/billOfSalePresentation';
import { PeekLink } from '../../components/PeekLink';
import { ID } from '../../components/ID';
import { PageChips } from './PageChips';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTdClass,
  extractionThClass,
  extractionTheadClass,
} from '../../components/reconstructedDocument/extractionTables/tableClasses';

interface BillOfSaleEventsProps {
  response: ApiEndpoints<string>['/api/projects/:id']['GET']['response'];
}

export function BillOfSaleEvents({ response }: BillOfSaleEventsProps) {
  const rawEvents = response.item.data?.bosEvents?.billOfSaleEvents;
  const [inspected, setInspected] = useState<BillOfSaleEventWithDocument | null>(null);

  if (!rawEvents || !rawEvents.length) {
    return null;
  }

  // A BoS event can arrive without pageIds (runtime violates the string[] type, e.g. while a
  // project is mid-reprocessing). Default to [] so .join / PageChips don't crash on undefined.
  const events = rawEvents.map((event) => ({ ...event, pageIds: event.pageIds ?? [] }));
  const csvData = events.map((event) => ({ ...event, pageIds: event.pageIds.join(' ') }));

  return (
    <div className="space-y-6">
      <Card
        header={
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">
            Bill of Sale / Commercial Trace
          </h2>
        }
      >
        <DescriptionList>
          <DescriptionTerm>Event Count</DescriptionTerm>
          <DescriptionDetails>{events.length}</DescriptionDetails>
        </DescriptionList>
      </Card>

      <ExtractionTableCard
        label="Bill of Sale / Commercial Trace"
        rows={csvData}
        csvTitle="Bill of Sale Events"
        bodyHeight="project"
      >
        <table className={extractionTableClass}>
          <thead className={extractionTheadClass}>
            <tr>
              <th className={extractionThClass}>Date</th>
              <th className={extractionThClass}>ESN</th>
              <th className={extractionThClass}>Seller</th>
              <th className={extractionThClass}>Purchaser</th>
              <th className={extractionThClass}>Signed</th>
              <th className={extractionThClass}>Source Pages</th>
              <th className={extractionThClass}>Document</th>
              <th className={extractionThClass}>Type</th>
            </tr>
          </thead>
          <tbody className={extractionTbodyClass}>
            {events.map((event, index) => (
              <tr
                key={`${event.reconstructedDocumentId}-${event.esn}-${index}`}
                onClick={() => setInspected(event)}
                className={extractionRowClass}
              >
                <td className={extractionTdClass}>{event.date ?? '—'}</td>
                <td className={`${extractionTdClass} font-mono`}>{event.esn ?? '—'}</td>
                <td className={extractionTdClass} title={event.seller ?? undefined}>
                  {event.seller ?? '—'}
                </td>
                <td className={extractionTdClass} title={event.purchaser ?? undefined}>
                  {event.purchaser ?? '—'}
                </td>
                <td className={extractionTdClass}>{formatBillOfSaleSigned(event.isDocumentSigned)}</td>
                <td className={extractionTdClass}>
                  <div onClick={(e) => e.stopPropagation()}>
                    <PageChips pageIds={event.pageIds} />
                  </div>
                </td>
                <td className={extractionTdClass}>
                  <div className="flex flex-col gap-0.5" onClick={(e) => e.stopPropagation()}>
                    {event.reconstructedDocumentId && (
                      <PeekLink
                        type="reconstructed"
                        id={event.reconstructedDocumentId}
                        className="inline-flex items-center gap-1"
                      >
                        <FaFile className="size-3" />
                        <ID id={event.reconstructedDocumentId} />
                      </PeekLink>
                    )}
                    {event.documentId && (
                      <PeekLink
                        type="document"
                        id={event.documentId}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-zinc-400"
                      >
                        <FaFile className="size-3" />
                        <ID id={event.documentId} />
                      </PeekLink>
                    )}
                    {!event.reconstructedDocumentId && !event.documentId && '—'}
                  </div>
                </td>
                <td className={extractionTdClass}>{event.eventType}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ExtractionTableCard>

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title="Bill of Sale row"
        data={inspected}
      />
    </div>
  );
}
